import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';
import { getSidebarItemsForRole } from '../components/SidebarPresets';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiconfig';

function Section({ title, children, right }) {
  return (
    <section className="bg-white rounded-xl shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function CompanyAdminDashboard() {
  const { user } = useAuth();
  const companyParam = user?.companyKey || user?.companyId;
  const sidebar = useSidebar();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('details');

  const [jobPosts, setJobPosts] = useState([]);
  const [jobPostsLoading, setJobPostsLoading] = useState(false);
  const [jobPostsError, setJobPostsError] = useState('');

  const [jobPostModalOpen, setJobPostModalOpen] = useState(false);
  const [selectedJobPost, setSelectedJobPost] = useState(null);

  const [recruiterModalOpen, setRecruiterModalOpen] = useState(false);
  const [recruiterModalLoading, setRecruiterModalLoading] = useState(false);
  const [recruiterModalError, setRecruiterModalError] = useState('');
  const [recruiterProfile, setRecruiterProfile] = useState(null);

  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);

  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [tab, setTab] = useState('approved');
  const [companyAdmins, setCompanyAdmins] = useState([]);
  const [clusterAdmins, setClusterAdmins] = useState([]);
  const [unitAdmins, setUnitAdmins] = useState([]);

  useEffect(() => {
    if (!companyParam) return;
    (async () => {
      try {
        setLoading(true);
        const [tRes, cRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/companies/${companyParam}/tree`),
          fetch(`${API_BASE_URL}/api/companies/${companyParam}`)
        ]);
        if (!tRes.ok) throw new Error('Failed to load tree');
        if (!cRes.ok) throw new Error('Failed to load company');
        setTree(await tRes.json());
        const c = await cRes.json();
        setCompany(c);
        setEditDraft(c);
      } catch (e) {
        setError(e.message || 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
  }, [companyParam]);

  async function loadRecruiters() {
    if (!companyParam) return;
    try {
      const [p, a, rj] = await Promise.all([
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/recruiters?scope=company&status=pending`).then(r=>r.json()),
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/recruiters?scope=company&status=approved`).then(r=>r.json()),
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/recruiters?scope=company&status=rejected`).then(r=>r.json())
      ]);
      setPending(Array.isArray(p)?p:[]);
      setApproved(Array.isArray(a)?a:[]);
      setRejected(Array.isArray(rj)?rj:[]);
    } catch {}
  }
  useEffect(()=>{ loadRecruiters(); }, [companyParam]);

  async function loadAdmins() {
    if (!companyParam) return;
    try {
      const [ca, cla, ua] = await Promise.all([
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/admins`).then(r=>r.json()),
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/cluster-admins`).then(r=>r.json()),
        fetch(`${API_BASE_URL}/api/companies/${companyParam}/unit-admins`).then(r=>r.json()),
      ]);
      setCompanyAdmins(Array.isArray(ca)?ca:[]);
      setClusterAdmins(Array.isArray(cla)?cla:[]);
      setUnitAdmins(Array.isArray(ua)?ua:[]);
    } catch (e) {
      // silent
    }
  }

  function openJobPostModal(jp) {
    if (!jp) return;
    setSelectedJobPost(jp);
    setJobPostModalOpen(true);
  }
  useEffect(()=>{ loadAdmins(); }, [companyParam]);

  async function loadJobPosts() {
    if (!companyParam) return;
    setJobPostsLoading(true);
    setJobPostsError('');
    try {
      const qs = new URLSearchParams({ scope: 'company', companyId: String(companyParam) });
      const res = await fetch(`${API_BASE_URL}/api/job-postings/admin?${qs.toString()}`);
      if (!res.ok) throw new Error('Failed to load job posts');
      const data = await res.json();
      setJobPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      setJobPostsError(e.message || 'Failed to load job posts');
      setJobPosts([]);
    } finally {
      setJobPostsLoading(false);
    }
  }

  async function deleteJobPost(id) {
    if (!companyParam || !id) return;
    const ok = window.confirm('Delete this job post?');
    if (!ok) return;
    try {
      const qs = new URLSearchParams({ scope: 'company', companyId: String(companyParam) });
      const res = await fetch(`${API_BASE_URL}/api/job-postings/${id}?${qs.toString()}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await loadJobPosts();
    } catch (e) {
      setJobPostsError(e.message || 'Delete failed');
    }
  }

  async function openRecruiterModal(r) {
    const userId = r?.user_id;
    if (!userId) return;
    setRecruiterModalOpen(true);
    setRecruiterModalLoading(true);
    setRecruiterModalError('');
    setRecruiterProfile(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${encodeURIComponent(String(userId))}`);
      if (!res.ok) throw new Error('Failed to load recruiter details');
      const data = await res.json();
      setRecruiterProfile(data);
    } catch (e) {
      setRecruiterModalError(e.message || 'Failed to load recruiter details');
    } finally {
      setRecruiterModalLoading(false);
    }
  }

  // Keep active tab in sync with URL hash (React Router updates may not trigger native hashchange)
  useEffect(() => {
    const allowed = new Set((getSidebarItemsForRole(user?.role) || []).map(i => i.id));
    const h = (location.hash || '').replace('#', '');
    if (h && allowed.has(h)) setActiveTab(h);
  }, [user?.role, location.hash]);

  // Default Recruiters view to Approved whenever entering Recruiters tab
  useEffect(() => {
    if (activeTab === 'recruiters') setTab('approved');
  }, [activeTab]);

  // Auto-load Job Posts when entering Job Posts tab
  useEffect(() => {
    if (activeTab === 'job-posts') loadJobPosts();
  }, [activeTab, companyParam]);

  async function saveCompany() {
    if (!editDraft) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        company_name: editDraft.company_name,
        industry: editDraft.industry,
        website: editDraft.website,
        description: editDraft.description,
        phone: editDraft.phone,
        address: editDraft.address,
        employee_count: editDraft.employee_count
      };
      const res = await fetch(`${API_BASE_URL}/api/company/${editDraft.id}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload)});
      if (!res.ok) throw new Error('Save failed');
      setCompany(editDraft);
      setEditOpen(false);
    } catch(e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function approveRecruiter(id) {
    await fetch(`${API_BASE_URL}/api/recruiters/${id}/approve`, { method:'POST' });
    loadRecruiters();
  }
  async function rejectRecruiter(id) {
    await fetch(`${API_BASE_URL}/api/recruiters/${id}/reject`, { method:'POST' });
    loadRecruiters();
  }

  if (!user || user.role !== 'company-admin') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 p-4 rounded">Company admin access required.</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-x-hidden">
      <Header />
      <main className="flex-1 overflow-hidden">
        <div className="flex h-full min-h-0 min-w-0">
          <Sidebar
            items={getSidebarItemsForRole(user?.role)}
            activeId={activeTab}
            onSelect={setActiveTab}
            isOpen={sidebar.isOpen}
            onOverlayClick={sidebar.close}
            onItemClick={sidebar.close}
          />
          <div className={`flex-1 min-w-0 flex flex-col min-h-0 ${sidebar.isOpen ? 'md:ml-64' : ''}`}>
            <div className="flex-1 min-w-0 p-4 md:p-6 overflow-y-auto">
        {error && <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 mb-3">{error}</div>}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Section title="Company Details" right={company && (<button className="px-3 py-2 rounded bg-indigo-600 text-white" onClick={()=>{ setEditDraft(company); setEditOpen(true); }}>Edit Details</button>)}>
              {company ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Name</div>
                    <div className="font-medium">{company.company_name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Industry</div>
                    <div className="font-medium">{company.industry || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Website</div>
                    <div className="font-medium">{company.website || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Phone</div>
                    <div className="font-medium">{company.phone || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Employees</div>
                    <div className="font-medium">{company.employee_count || '-'}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-sm text-gray-600 mb-1">Address</div>
                    <div className="font-medium">{company.address || '-'}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-sm text-gray-600 mb-1">Description</div>
                    <div className="font-medium whitespace-pre-line">{company.description || '-'}</div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">No data</div>
              )}
            </Section>
          </div>
          <div></div>
        </div>
        )}

        {activeTab === 'admins' && (
          <div className="grid grid-cols-1 gap-6">
            <Section title="Admins - Company">
              <div className="overflow-x-auto">
                <table className="min-w-full border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-3 py-2 text-left">Scope</th>
                      <th className="border px-3 py-2 text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyAdmins.map(a => (
                      <tr key={`cadmin-${a.company_id}-${a.email}`} className="odd:bg-white even:bg-gray-50">
                        <td className="border px-3 py-2">Company</td>
                        <td className="border px-3 py-2">{a.email}</td>
                      </tr>
                    ))}
                    {companyAdmins.length===0 && (
                      <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={2}>No company admins</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Admins - Clusters">
              <div className="overflow-x-auto">
                <table className="min-w-full border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-3 py-2 text-left">Cluster</th>
                      <th className="border px-3 py-2 text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clusterAdmins.map(a => (
                      <tr key={`cladmin-${a.cluster_id}-${a.email}`} className="odd:bg-white even:bg-gray-50">
                        <td className="border px-3 py-2">{a.cluster_name}</td>
                        <td className="border px-3 py-2">{a.email}</td>
                      </tr>
                    ))}
                    {clusterAdmins.length===0 && (
                      <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={2}>No cluster admins</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Admins - Units">
              <div className="overflow-x-auto">
                <table className="min-w-full border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border px-3 py-2 text-left">Unit</th>
                      <th className="border px-3 py-2 text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitAdmins.map(a => (
                      <tr key={`uadmin-${a.unit_id}-${a.email}`} className="odd:bg-white even:bg-gray-50">
                        <td className="border px-3 py-2">{a.unit_name}</td>
                        <td className="border px-3 py-2">{a.email}</td>
                      </tr>
                    ))}
                    {unitAdmins.length===0 && (
                      <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={2}>No unit admins</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>

          </div>
        )}

        {activeTab === 'structure' && (
          <div className="grid grid-cols-1 gap-6">
            <Section title="Company Structure">
              {!tree ? (
                <div className="text-gray-500">{loading?'Loading...':'No data'}</div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{tree.company.name}</div>
                    <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200">You are Company Admin</span>
                  </div>
                  <ul className="pl-4 list-disc">
                    {tree.clusters.map(c => (
                      <li key={c.id}>
                        <div className="font-medium">{c.name}</div>
                        {c.units.length>0 && (
                          <ul className="pl-5 list-[circle]">
                            {c.units.map(u => (
                              <li key={u.id}>{u.name}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                  {tree.unitsWithoutCluster?.length>0 && (
                    <div>
                      <div className="text-sm text-gray-600 mt-2">Units without cluster</div>
                      <ul className="pl-5 list-[square]">
                        {tree.unitsWithoutCluster.map(u => (
                          <li key={u.id}>{u.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Section>
          </div>
        )}

        {activeTab === 'job-posts' && (
          <div className="grid grid-cols-1 gap-6">
            <Section
              title="Job Posts"
              right={<button className="px-3 py-1.5 rounded bg-gray-100" onClick={loadJobPosts}>Refresh</button>}
            >
              {jobPostsError && (
                <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 mb-3">{jobPostsError}</div>
              )}
              {jobPostsLoading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border px-3 py-2 text-left">Title</th>
                        <th className="border px-3 py-2 text-left">Recruiter</th>
                        <th className="border px-3 py-2 text-left">Status</th>
                        <th className="border px-3 py-2 text-left">Posted Date</th>
                        <th className="border px-3 py-2 text-left">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobPosts.map(jp => (
                        <tr key={jp.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border px-3 py-2">
                            <button className="text-indigo-600 hover:underline" onClick={() => openJobPostModal(jp)}>
                              {jp.title || '-'}
                            </button>
                          </td>
                          <td className="border px-3 py-2">{jp.recruiter_name || jp.recruiter_email || '-'}</td>
                          <td className="border px-3 py-2">{jp.status || '-'}</td>
                          <td className="border px-3 py-2">{jp.post_date ? String(jp.post_date).slice(0, 10) : '-'}</td>
                          <td className="border px-3 py-2">
                            <button className="px-2 py-1 text-sm bg-red-600 text-white rounded" onClick={()=>deleteJobPost(jp.id)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {jobPosts.length === 0 && (
                        <tr>
                          <td className="border px-3 py-4 text-center text-gray-500" colSpan={5}>No job posts</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}

        {activeTab === 'clusters' && (
          <div className="grid grid-cols-1 gap-6">
            <Section title="Clusters">
              {!tree ? (
                <div className="text-gray-500">{loading?'Loading...':'No data'}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tree?.clusters || []).map(c => (
                        <tr key={c.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border px-3 py-2">{c.name}</td>
                          <td className="border px-3 py-2">{c.units?.length || 0}</td>
                        </tr>
                      ))}
                      {(tree?.clusters || []).length===0 && (
                        <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={3}>No clusters</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}

        {activeTab === 'units' && (
          <div className="grid grid-cols-1 gap-6">
            <Section title="Units">
              {!tree ? (
                <div className="text-gray-500">{loading?'Loading...':'No data'}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Cluster</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const rows = [];
                        (tree?.clusters || []).forEach(c => (c.units||[]).forEach(u => rows.push({ ...u, clusterName: c.name })));
                        (tree?.unitsWithoutCluster || []).forEach(u => rows.push({ ...u, clusterName: '-' }));
                        return rows;
                      })().map(u => (
                        <tr key={u.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border px-3 py-2">{u.name}</td>
                          <td className="border px-3 py-2">{u.clusterName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}

        {activeTab === 'recruiters' && (
          <div className="grid grid-cols-1 gap-6">
            <Section title="Recruiters">
              <div className="mb-3 flex gap-2">
                <button className={`px-3 py-1.5 rounded ${tab==='pending'?'bg-indigo-600 text-white':'bg-gray-100'}`} onClick={()=>setTab('pending')}>Pending Approvals</button>
                <button className={`px-3 py-1.5 rounded ${tab==='approved'?'bg-indigo-600 text-white':'bg-gray-100'}`} onClick={()=>setTab('approved')}>Approved</button>
                <button className={`px-3 py-1.5 rounded ${tab==='rejected'?'bg-indigo-600 text-white':'bg-gray-100'}`} onClick={()=>setTab('rejected')}>Rejected</button>
                <button className="ml-auto px-3 py-1.5 rounded bg-gray-100" onClick={loadRecruiters}>Refresh</button>
              </div>
              {tab==='pending' ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Email</th>
                        <th className="border px-3 py-2 text-left">Phone</th>
                        <th className="border px-3 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map(r=> (
                        <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border px-3 py-2">
                            <button className="text-indigo-600 hover:underline" onClick={()=>openRecruiterModal(r)}>
                              {r.first_name || ''} {r.last_name || ''}
                            </button>
                          </td>
                          <td className="border px-3 py-2">{r.work_email || '-'}</td>
                          <td className="border px-3 py-2">{r.phone || '-'}</td>
                          <td className="border px-3 py-2">
                            <button className="px-2 py-1 text-sm bg-green-600 text-white rounded mr-2" onClick={()=>approveRecruiter(r.id)}>Approve</button>
                            <button className="px-2 py-1 text-sm bg-red-600 text-white rounded" onClick={()=>rejectRecruiter(r.id)}>Reject</button>
                          </td>
                        </tr>
                      ))}
                      {pending.length===0 && (
                        <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={4}>No pending approvals</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : tab==='approved' ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Email</th>
                        <th className="border px-3 py-2 text-left">Phone</th>
                        <th className="border px-3 py-2 text-left">Account Status</th>
                        <th className="border px-3 py-2 text-left">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approved.map(r=> (
                        <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border px-3 py-2">
                            <button className="text-indigo-600 hover:underline" onClick={()=>openRecruiterModal(r)}>
                              {r.first_name || ''} {r.last_name || ''}
                            </button>
                          </td>
                          <td className="border px-3 py-2">{r.work_email || '-'}</td>
                          <td className="border px-3 py-2">{r.phone || '-'}</td>
                          <td className="border px-3 py-2">{r.account_status || 'active'}</td>
                          <td className="border px-3 py-2">
                            {r.account_status === 'active' ? (
                              <button className="px-2 py-1 text-sm bg-yellow-50 text-yellow-700 rounded" onClick={async()=>{ await fetch(`${API_BASE_URL}/api/recruiters/${r.id}/deactivate`, { method: 'POST' }); await loadRecruiters(); }}>Set Inactive</button>
                            ) : (
                              <button className="px-2 py-1 text-sm bg-green-600 text-white rounded" onClick={async()=>{ await fetch(`${API_BASE_URL}/api/recruiters/${r.id}/activate`, { method: 'POST' }); await loadRecruiters(); }}>Set Active</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {approved.length===0 && (
                        <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={5}>No members</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border px-3 py-2 text-left">Name</th>
                        <th className="border px-3 py-2 text-left">Email</th>
                        <th className="border px-3 py-2 text-left">Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejected.map(r=> (
                        <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                          <td className="border px-3 py-2">
                            <button className="text-indigo-600 hover:underline" onClick={()=>openRecruiterModal(r)}>
                              {r.first_name || ''} {r.last_name || ''}
                            </button>
                          </td>
                          <td className="border px-3 py-2">{r.work_email || '-'}</td>
                          <td className="border px-3 py-2">{r.phone || '-'}</td>
                        </tr>
                      ))}
                      {rejected.length===0 && (
                        <tr><td className="border px-3 py-4 text-center text-gray-500" colSpan={3}>No rejected</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>
        )}
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Edit Company Modal */}
      {editOpen && editDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Company Details</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={()=>setEditOpen(false)}>✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Name</label>
                <input className="border rounded p-2 w-full" value={editDraft.company_name || ''} onChange={e=>setEditDraft({...editDraft, company_name:e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Industry</label>
                <input className="border rounded p-2 w-full" value={editDraft.industry || ''} onChange={e=>setEditDraft({...editDraft, industry:e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Website</label>
                <input className="border rounded p-2 w-full" value={editDraft.website || ''} onChange={e=>setEditDraft({...editDraft, website:e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone</label>
                <input className="border rounded p-2 w-full" value={editDraft.phone || ''} onChange={e=>setEditDraft({...editDraft, phone:e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Employees</label>
                <select
                  className="border rounded p-2 w-full"
                  value={editDraft.employee_count || ''}
                  onChange={e=>setEditDraft({ ...editDraft, employee_count: e.target.value })}
                >
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-1000">201-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Address</label>
                <input className="border rounded p-2 w-full" value={editDraft.address || ''} onChange={e=>setEditDraft({...editDraft, address:e.target.value})} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Description</label>
                <textarea className="border rounded p-2 w-full" rows={3} value={editDraft.description || ''} onChange={e=>setEditDraft({...editDraft, description:e.target.value})} />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={()=>setEditOpen(false)}>Cancel</button>
              <button className="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50" disabled={saving} onClick={saveCompany}>{saving?'Saving...':'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {recruiterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recruiter Details</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={()=>{ setRecruiterModalOpen(false); setRecruiterProfile(null); }}>✕</button>
            </div>
            {recruiterModalError && (
              <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 mb-3">{recruiterModalError}</div>
            )}
            {recruiterModalLoading ? (
              <div className="text-gray-500">Loading...</div>
            ) : recruiterProfile ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Name</div>
                  <div className="font-medium">{`${recruiterProfile.first_name || ''} ${recruiterProfile.last_name || ''}`.trim() || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Work Email</div>
                  <div className="font-medium">{recruiterProfile.work_email || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Phone</div>
                  <div className="font-medium">{recruiterProfile.phone || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Position</div>
                  <div className="font-medium">{recruiterProfile.position || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Department</div>
                  <div className="font-medium">{recruiterProfile.department || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Account Status</div>
                  <div className="font-medium">{recruiterProfile.account_status || '-'}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-600 mb-1">Address</div>
                  <div className="font-medium">{recruiterProfile.location_country ? [recruiterProfile.village, recruiterProfile.grama_niladhari_division, recruiterProfile.divisional_secretariat, recruiterProfile.district, recruiterProfile.province, recruiterProfile.location_country].filter(Boolean).join(', ') : '-'}</div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500">No data</div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={()=>{ setRecruiterModalOpen(false); setRecruiterProfile(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {jobPostModalOpen && selectedJobPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Job Post Details</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => { setJobPostModalOpen(false); setSelectedJobPost(null); }}>✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <div className="text-sm text-gray-600 mb-1">Title</div>
                <div className="font-medium">{selectedJobPost.title || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Recruiter</div>
                <div className="font-medium">{selectedJobPost.recruiter_name || selectedJobPost.recruiter_email || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Status</div>
                <div className="font-medium">{selectedJobPost.status || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Posted Date</div>
                <div className="font-medium">{selectedJobPost.post_date ? String(selectedJobPost.post_date).slice(0, 10) : '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">Location</div>
                <div className="font-medium">{selectedJobPost.location || selectedJobPost.job_location || '-'}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-sm text-gray-600 mb-1">Description</div>
                <div className="font-medium whitespace-pre-line">{selectedJobPost.description || selectedJobPost.job_description || '-'}</div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button className="px-4 py-2 rounded border" onClick={() => { setJobPostModalOpen(false); setSelectedJobPost(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
